import { ContractLanguageSection } from "components/ContractLanguageSection";
import { IRuleAttachment } from "components/ContractLanguageSection/types";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";

export interface IExplodingFeeScheduleFormData {
  providerId: string | null;
  payerId: number | null;
  cptCodes: string[];
  totalRateInCents: number | null;
  groupName: string;
  isDefault?: boolean;
  contractLanguage: string;
}

export const INITIAL_FORM_DATA: IExplodingFeeScheduleFormData = {
  providerId: null,
  payerId: null,
  cptCodes: [],
  totalRateInCents: null,
  groupName: "",
  isDefault: false,
  contractLanguage: "",
};

interface IExplodingFeeScheduleFormProps {
  title: string;
  submitLabel: string;
  submitIcon: string;
  formData: IExplodingFeeScheduleFormData;
  onChange: (data: IExplodingFeeScheduleFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** Rule id — present in Edit mode, enables attachments. */
  ruleId?: string;
  attachments?: IRuleAttachment[];
}

export function ExplodingFeeScheduleForm({
  title,
  submitLabel,
  submitIcon,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  ruleId,
  attachments,
}: IExplodingFeeScheduleFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    if (!payers?.data) return [];
    return payers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [payers]);

  const handleInputChange = (
    field: keyof IExplodingFeeScheduleFormData,
    value: any
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const toggleDefault = (checked: boolean) => {
    // When switching to default, clear provider/payer so they aren't submitted.
    onChange({
      ...formData,
      isDefault: checked,
      providerId: checked ? null : formData.providerId,
      payerId: checked ? null : formData.payerId,
    });
  };

  const isValid =
    (formData.isDefault || (formData.providerId && formData.payerId)) &&
    formData.cptCodes.length >= 2 &&
    formData.cptCodes.length <= 15 &&
    formData.totalRateInCents != null &&
    formData.totalRateInCents > 0;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Card className="mb-4">
        <div
          className="flex align-items-center gap-2 p-3 mb-3"
          style={{ background: "#f3f4f6", borderRadius: 6 }}
        >
          <Checkbox
            inputId="isDefault"
            checked={!!formData.isDefault}
            onChange={(e) => toggleDefault(!!e.checked)}
          />
          <label
            htmlFor="isDefault"
            style={{ fontWeight: 600, cursor: "pointer" }}
          >
            Global default rule
          </label>
          <span style={{ color: "#6b7280", fontSize: 12, marginLeft: 8 }}>
            No provider or payer. Other contracts can copy this rule.
          </span>
        </div>
        <div className="grid">
          {!formData.isDefault && (
            <>
              <div className="col-12 md:col-6 field">
                <label htmlFor="provider">Provider *</label>
                <Dropdown
                  id="provider"
                  className="w-full"
                  value={formData.providerId}
                  options={providerOptions}
                  onChange={(e) => handleInputChange("providerId", e.value)}
                  placeholder="Select Provider"
                  filter
                />
              </div>
              <div className="col-12 md:col-6 field">
                <label htmlFor="payer">Payer *</label>
                <Dropdown
                  id="payer"
                  className="w-full"
                  value={formData.payerId}
                  options={payerOptions}
                  onChange={(e) => handleInputChange("payerId", e.value)}
                  placeholder="Select Payer"
                  filter
                />
              </div>
            </>
          )}
          <div className="col-12 md:col-6 field">
            <label htmlFor="groupName">Group Name</label>
            <InputText
              id="groupName"
              className="w-full"
              value={formData.groupName}
              onChange={(e) => handleInputChange("groupName", e.target.value)}
              placeholder="e.g., Orthopedic Bundle"
            />
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="totalRate">Total Rate *</label>
            <InputNumber
              id="totalRate"
              className="w-full"
              value={
                formData.totalRateInCents != null
                  ? formData.totalRateInCents / 100
                  : undefined
              }
              onValueChange={(e) =>
                handleInputChange(
                  "totalRateInCents",
                  e.value != null ? Math.round(e.value * 100) : null
                )
              }
              mode="currency"
              currency="USD"
              locale="en-US"
              min={0.01}
              placeholder="e.g., $1,200.00"
            />
            <small className="text-500">
              Total amount paid across all CPT codes in the ruleset
            </small>
          </div>
          <div className="col-12 field">
            <label htmlFor="cptCodes">CPT Codes * (min 2, max 15)</label>
            <Chips
              id="cptCodes"
              className="w-full"
              value={formData.cptCodes}
              onChange={(e) => handleInputChange("cptCodes", e.value || [])}
              placeholder="Type a CPT code and press Enter"
              separator=","
            />
            <small className="text-500">
              Press Enter or type comma to add each CPT code.
              {formData.totalRateInCents != null &&
                formData.cptCodes.length >= 2 && (
                  <>
                    {" "}
                    Per-code rate: $
                    {(
                      formData.totalRateInCents /
                      100 /
                      formData.cptCodes.length
                    ).toFixed(2)}
                  </>
                )}
            </small>
          </div>
        </div>
      </Card>

      <ContractLanguageSection
        ruleType="EXPLODING_FEE"
        ruleId={ruleId}
        attachments={attachments}
        value={formData.contractLanguage}
        onChange={(v) => handleInputChange("contractLanguage", v)}
      />

      <div className="flex justify-content-end gap-2">
        <Button
          label="Cancel"
          className="p-button-outlined"
          onClick={() => navigate("/payer-rules")}
        />
        <Button
          label={submitLabel}
          icon={submitIcon}
          disabled={!isValid || isSubmitting}
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
